import type {
  DashboardHistoricPenaltyYear,
  FacilityIndividualDashboardResponse,
  FacilityNetworkOverviewResponse,
} from "@services/facilityDashboard";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import { abbreviateFacilityType } from "./facilityNetworkChildrenUtils";

export type OverviewFacilityDashboardInput = {
  facilityId: number;
  name: string;
  typeLabel: string;
  location: string;
  metaLine: string;
  dashboard: FacilityIndividualDashboardResponse | null;
};

export type OverviewTopExposureRow = {
  facilityId: number;
  name: string;
  metaLine: string;
  exposure: number;
  exposureDisplay: string;
  dashboardFacility: MyFacilityListRow;
};

export type OverviewTrendYear = {
  year: number;
  amount: number;
};

export type OverviewAggregateResult = {
  totalPenaltyExposure: number;
  totalPenaltyDisplay: string;
  facilitiesWithGaps: number;
  facilitiesClean: number;
  surveysDue90d: number;
  gapsByType: Record<string, number>;
  topExposure: OverviewTopExposureRow[];
  trendYears: OverviewTrendYear[];
  loadedCount: number;
  failedCount: number;
};

export function formatOverviewCurrency(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return "$0";
  if (amount >= 1_000_000) {
    const m = amount / 1_000_000;
    const s = (m >= 10 ? m.toFixed(0) : m.toFixed(1)).replace(/\.0$/, "");
    return `$${s}M`;
  }
  if (amount >= 1_000) {
    const k = amount / 1_000;
    const s = k.toFixed(1).replace(/\.0$/, "");
    return `$${s}k`;
  }
  return `$${Math.round(amount).toLocaleString("en-US")}`;
}

function facilityHasGaps(dashboard: FacilityIndividualDashboardResponse): boolean {
  const actions = dashboard.action_required || [];
  if (actions.length > 0) return true;
  const overdueEvents = (dashboard.calendar_strip?.events || []).filter(
    (event) => event.status === "overdue",
  );
  if (overdueEvents.length > 0) return true;
  const completeness = dashboard.profile_completeness?.completeness_percentage;
  if (typeof completeness === "number" && completeness < 100) return true;
  return false;
}

function facilityIsClean(dashboard: FacilityIndividualDashboardResponse): boolean {
  const exposure = dashboard.tiles?.penalty_exposure?.total_exposure ?? 0;
  const facilityHist = dashboard.historic_penalties_facility;
  const recentPenalty =
    facilityHist?.yearly_totals?.some((year) => {
      const currentYear = new Date().getFullYear();
      return year.year >= currentYear - 1 && year.amount > 0;
    }) ?? false;
  return exposure <= 0 && !recentPenalty;
}

function countSurveysDue90d(dashboard: FacilityIndividualDashboardResponse): number {
  const now = Date.now();
  const horizon = now + 90 * 24 * 60 * 60 * 1000;
  return (dashboard.calendar_strip?.events || []).filter((event) => {
    if (event.status === "overdue") return true;
    if (!event.date) return false;
    const ts = new Date(event.date).getTime();
    if (Number.isNaN(ts)) return false;
    return ts >= now && ts <= horizon;
  }).length;
}

function mergeTrendYears(
  inputs: OverviewFacilityDashboardInput[],
): OverviewTrendYear[] {
  const byYear = new Map<number, number>();
  inputs.forEach((row) => {
    const years = row.dashboard?.historic_penalties_facility?.yearly_totals || [];
    years.forEach((year: DashboardHistoricPenaltyYear) => {
      byYear.set(year.year, (byYear.get(year.year) || 0) + (year.amount || 0));
    });
  });
  return Array.from(byYear.entries())
    .map(([year, amount]) => ({ year, amount }))
    .sort((a, b) => a.year - b.year)
    .slice(-6);
}

function toDashboardFacility(row: OverviewFacilityDashboardInput): MyFacilityListRow {
  const [city = "", state = ""] = row.location.split(",").map((part) => part.trim());
  return {
    facility_id: row.facilityId,
    id: row.facilityId,
    facility_name: row.name,
    city,
    state,
    full_address: row.location,
    facility_type: row.typeLabel,
    org_id: 0,
    org_name: "",
    is_child_facility: true,
  };
}

/** Map network-overview API payload into Overview UI model. */
export function mapNetworkOverviewApiToAggregate(
  api: FacilityNetworkOverviewResponse,
): OverviewAggregateResult {
  return {
    totalPenaltyExposure: api.total_penalty_exposure ?? 0,
    totalPenaltyDisplay: api.total_penalty_display || "$0",
    facilitiesWithGaps: api.facilities_with_gaps ?? 0,
    facilitiesClean: api.facilities_clean ?? 0,
    surveysDue90d: api.surveys_due_90d ?? 0,
    gapsByType: api.gaps_by_type || {},
    topExposure: (api.top_exposure || []).map((row) => {
      const typeLabel = abbreviateFacilityType({
        facility_type: row.facility_type || row.type_label || "",
      });
      const metaParts = (row.meta_line || "").split(" · ").filter(Boolean);
      const metaLine =
        metaParts.length >= 2
          ? [typeLabel, ...metaParts.slice(1)].join(" · ")
          : `${typeLabel} · ${row.location || "—"}`;
      return {
        facilityId: row.facility_id,
        name: row.name,
        metaLine,
        exposure: row.exposure ?? 0,
        exposureDisplay: row.exposure_display || formatOverviewCurrency(row.exposure ?? 0),
        dashboardFacility: {
          facility_id: row.facility_id,
          id: row.facility_id,
          facility_name: row.name,
          city: row.city || "",
          state: row.state || "",
          full_address: row.location || "",
          facility_type: row.facility_type || typeLabel,
          org_id: 0,
          org_name: "",
          is_child_facility: true,
        },
      };
    }),
    trendYears: (api.trend_years || []).map((year) => ({
      year: year.year,
      amount: year.amount ?? 0,
    })),
    loadedCount: api.loaded_count ?? 0,
    failedCount: api.failed_count ?? 0,
  };
}

/** Aggregate linked-child individual dashboards for Facility Network Overview. */
export function aggregateFacilityNetworkOverview(
  inputs: OverviewFacilityDashboardInput[],
): OverviewAggregateResult {
  const loaded = inputs.filter((row) => row.dashboard != null);
  const failedCount = inputs.length - loaded.length;

  let totalPenaltyExposure = 0;
  let facilitiesWithGaps = 0;
  let facilitiesClean = 0;
  let surveysDue90d = 0;
  const gapsByType: Record<string, number> = {};

  const topCandidates: OverviewTopExposureRow[] = [];

  loaded.forEach((row) => {
    const dashboard = row.dashboard!;
    const exposure = Number(dashboard.tiles?.penalty_exposure?.total_exposure ?? 0) || 0;
    totalPenaltyExposure += exposure;

    if (facilityHasGaps(dashboard)) {
      facilitiesWithGaps += 1;
      gapsByType[row.typeLabel] = (gapsByType[row.typeLabel] || 0) + 1;
    }
    if (facilityIsClean(dashboard)) {
      facilitiesClean += 1;
    }
    surveysDue90d += countSurveysDue90d(dashboard);

    topCandidates.push({
      facilityId: row.facilityId,
      name: row.name,
      metaLine: row.metaLine,
      exposure,
      exposureDisplay: formatOverviewCurrency(exposure),
      dashboardFacility: toDashboardFacility(row),
    });
  });

  topCandidates.sort(
    (a, b) => b.exposure - a.exposure || a.name.localeCompare(b.name),
  );

  return {
    totalPenaltyExposure,
    totalPenaltyDisplay: formatOverviewCurrency(totalPenaltyExposure),
    facilitiesWithGaps,
    facilitiesClean,
    surveysDue90d,
    gapsByType,
    topExposure: topCandidates.slice(0, 5),
    trendYears: mergeTrendYears(loaded),
    loadedCount: loaded.length,
    failedCount,
  };
}
